import { useState, useMemo } from 'react';
import { useTags } from '../hooks/useTags';
import { tagsApi } from '../api/tags';
import {
  TAG_GROUPS,
  type TagGroupKey,
  getTagGroup,
  getTagLabel,
  getTagColorClass,
} from '../utils/tagUtils';
import type { Tag } from '../types';

export default function TagManagementPage() {
  const { data: tagsData, refetch } = useTags();
  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Filter tags with use_count == 0
  const unusedTags = useMemo(() => {
    return tagsData?.tags.filter((tag) => (tag.use_count ?? 0) === 0) ?? [];
  }, [tagsData]);

  // Group unused tags
  const groupedUnusedTags = useMemo(() => {
    const groups: Record<TagGroupKey, Tag[]> = {
      style: [],
      mood: [],
      other: [],
    };

    unusedTags.forEach((tag) => {
      groups[getTagGroup(tag.name)].push(tag);
    });

    (Object.keys(groups) as TagGroupKey[]).forEach((key) => {
      groups[key].sort((a, b) => a.name.localeCompare(b.name));
    });

    return groups;
  }, [unusedTags]);

  const handleToggleTag = (tagId: string) => {
    setSelectedTags((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(tagId)) {
        newSet.delete(tagId);
      } else {
        newSet.add(tagId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedTags.size === unusedTags.length) {
      setSelectedTags(new Set());
    } else {
      setSelectedTags(new Set(unusedTags.map((tag) => tag.id)));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedTags.size === 0) {
      setError('No tags selected');
      return;
    }

    const confirmed = window.confirm(
      `Delete ${selectedTags.size} tag(s)? This action cannot be undone.`
    );

    if (!confirmed) return;

    setIsDeleting(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const deletePromises = Array.from(selectedTags).map((tagId) =>
        tagsApi.deleteTag(tagId)
      );

      const results = await Promise.allSettled(deletePromises);
      const failures = results.filter((r) => r.status === 'rejected');

      if (failures.length > 0) {
        const firstError = failures[0] as PromiseRejectedResult;
        setError(
          firstError.reason instanceof Error
            ? firstError.reason.message
            : `${failures.length}件の削除に失敗しました`
        );
      } else {
        setSuccessMessage(`Successfully deleted ${selectedTags.size} tag(s)`);
        setSelectedTags(new Set());
      }

      // 一部成功した場合もリストを更新
      refetch();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to delete tags'
      );
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-6xl mx-auto p-4 md:p-8">
        <h1 className="text-3xl font-bold mb-2">Tag Management</h1>
        <p className="text-gray-600 mb-6">
          Manage unused tags. Delete tags that are no longer in use.
        </p>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded text-red-800">
            {error}
          </div>
        )}

        {successMessage && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded text-green-800">
            {successMessage}
          </div>
        )}

        {unusedTags.length === 0 ? (
          <div className="p-8 bg-blue-50 border border-blue-200 rounded text-center text-blue-800">
            <p className="text-lg">All tags are in use! No unused tags to clean up.</p>
          </div>
        ) : (
          <>
            <div className="mb-6 flex items-center justify-between">
              <div>
                <p className="text-gray-600">
                  Found {unusedTags.length} unused tag{unusedTags.length !== 1 ? 's' : ''}
                </p>
                <p className="text-sm text-gray-500">
                  {selectedTags.size} selected
                </p>
              </div>
              <div className="space-x-2">
                <button
                  onClick={handleSelectAll}
                  className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-100 text-sm font-medium"
                >
                  {selectedTags.size === unusedTags.length ? 'Deselect All' : 'Select All'}
                </button>
                <button
                  onClick={handleDeleteSelected}
                  disabled={selectedTags.size === 0 || isDeleting}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:bg-gray-400 text-sm font-medium"
                >
                  {isDeleting ? 'Deleting...' : 'Delete Selected'}
                </button>
              </div>
            </div>

            {/* Tags grouped by category */}
            {TAG_GROUPS.map((group) => {
              const tags = groupedUnusedTags[group.key];
              if (tags.length === 0) return null;

              return (
                <div key={group.key} className="mb-8">
                  <h2 className="text-lg font-semibold text-gray-700 mb-4 uppercase tracking-wide">
                    {group.label}
                  </h2>
                  <div className="space-y-2">
                    {tags.map((tag) => (
                      <div
                        key={tag.id}
                        className="flex items-center p-3 border border-gray-200 rounded hover:bg-gray-50"
                      >
                        <input
                          type="checkbox"
                          id={tag.id}
                          checked={selectedTags.has(tag.id)}
                          onChange={() => handleToggleTag(tag.id)}
                          className="w-4 h-4 text-red-600 rounded"
                        />
                        <label
                          htmlFor={tag.id}
                          className="ml-3 flex-1 cursor-pointer text-gray-800"
                        >
                          <div className="flex items-center gap-2">
                            <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-medium ${getTagColorClass(tag.name)}`}>
                              {getTagLabel(tag.name)}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            ID: {tag.id.substring(0, 8)}
                            {tag.created_at && ` • Created: ${new Date(tag.created_at).toLocaleDateString()}`}
                          </div>
                        </label>
                        <div className="text-sm text-gray-600 ml-4">
                          use_count: {tag.use_count ?? 0}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}
